import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Plus, Trash2 } from "lucide-react";

import { API_EMPLOYEE_CERTIFICATION } from "../../../../config/Endpoints.js";
import {
  Avatar,
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  FileField,
  Modal,
  Select,
  Spinner,
  TextField,
} from "../../../components/ui/index.ts";
import { useEmployeePhotoSource } from "../../../components/features/employee-view-directory/hooks/use-employee-photo-source.ts";
import { deleteApi, getApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { CertificationDocumentViewer } from "../../../components/features/certifications/certification-document-viewer.tsx";
import { useCertificationDocument } from "../../../components/features/certifications/use-certification-document.ts";
import type {
  CertificationCategory,
  EditableCertification,
  EmployeeCertificationRow,
} from "./certification-types.ts";
import styles from "./certification-editor-modal.module.css";

type Props = {
  employee: EmployeeCertificationRow | null;
  categories: CertificationCategory[];
  onClose: () => void;
  onSaved: () => void;
};

function parseDateOnly(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : null;
}

function formatDateOnly(value: Date | null): string {
  if (!value) return "";
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
}

function certificateNumberDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

function formatLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function CertificationEditorModal({
  employee,
  categories,
  onClose,
  onSaved,
}: Props) {
  const [certifications, setCertifications] = useState<EditableCertification[]>(
    [],
  );
  const [category, setCategory] = useState("");
  const [certificationId, setCertificationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [certificateFiles, setCertificateFiles] = useState<
    Record<number, File | null>
  >({});
  const submittingRef = useRef(false);
  const token = accessToken();
  const certificateDocument = useCertificationDocument(token);
  const employeePhotoSource = useEmployeePhotoSource(
    employee?.photoPath ?? undefined,
  );

  useEffect(() => {
    if (!employee) return;
    const controller = new AbortController();
    setLoading(true);
    getApi<EmployeeCertificationRow>(
      `${API_EMPLOYEE_CERTIFICATION}/${encodeURIComponent(
        String(employee.employeeId),
      )}`,
      accessToken(),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const payload =
          data && typeof data === "object" && "data" in data
            ? ((data as EmployeeCertificationRow & {
                data?: EmployeeCertificationRow;
              }).data ?? data)
            : data;
        setCertifications(
          (payload.certifications ?? []).map((item) => ({
            employeeCertificationId: item.employeeCertificationId,
            certificationId: item.certificationId,
            category: item.category,
            certification: item.certification,
            issuedBy: item.issuedBy ?? "",
            certificateNumber: item.certificateNumber ?? "",
            issueDate: item.issueDate ?? "",
            expiryDate: item.expiryDate ?? "",
            certificateUrl: item.certificateUrl ?? "",
            documentPath: item.documentPath ?? null,
            certificateUploaded: item.certificateUploaded ?? false,
          })),
        );
        setCertificateFiles({});
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          showError("Failed to load employee certifications.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    setCategory("");
    setCertificationId("");
    return () => controller.abort();
  }, [employee]);

  const categoryOptions = useMemo(
    () =>
      categories.map((item) => ({
        value: item.category,
        label: formatLabel(item.category),
      })),
    [categories],
  );

  const certificationOptions = useMemo(() => {
    const assigned = new Set(
      certifications.map((item) => item.certificationId),
    );
    return (
      categories
        .find((item) => item.category === category)
        ?.certifications.filter(
          (item) => !assigned.has(item.certificationId),
        )
        .map((item) => ({
          value: String(item.certificationId),
          label: item.certificationName,
        })) ?? []
    );
  }, [categories, category, certifications]);

  const addCertification = () => {
    const selectedCategory = categories.find(
      (item) => item.category === category,
    );
    const selected = selectedCategory?.certifications.find(
      (item) => String(item.certificationId) === certificationId,
    );
    if (!selected || !selectedCategory) {
      showError("Select a category and certification to add.");
      return;
    }
    setCertifications((current) => [
      ...current,
      {
        certificationId: selected.certificationId,
        category: selectedCategory.category,
        certification: selected.certificationName,
        issuedBy: "",
        certificateNumber: "",
        issueDate: "",
        expiryDate: "",
        certificateUrl: "",
      },
    ]);
    setCategory("");
    setCertificationId("");
  };

  const updateField = (
    index: number,
    field: keyof EditableCertification,
    value: string,
  ) => {
    setCertifications((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const removeCertification = async (
    item: EditableCertification,
    index: number,
  ) => {
    if (item.employeeCertificationId == null) {
      setCertifications((current) =>
        current.filter((_, itemIndex) => itemIndex !== index),
      );
      return;
    }
    setDeletingIds((current) =>
      new Set(current).add(item.employeeCertificationId as number),
    );
    try {
      await deleteApi(
        `${API_EMPLOYEE_CERTIFICATION}/${encodeURIComponent(
          String(item.employeeCertificationId),
        )}`,
        accessToken(),
      );
      setCertifications((current) =>
        current.filter(
          (entry) =>
            entry.employeeCertificationId !== item.employeeCertificationId,
        ),
      );
      showSuccess("Certification deleted successfully.");
      onSaved();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Failed to delete certification.",
      );
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current);
        next.delete(item.employeeCertificationId as number);
        return next;
      });
    }
  };

  const save = async () => {
    if (!employee || submittingRef.current) return;
    const missingRequired = certifications.find(
      (item) => !item.certificateNumber.trim() || !item.issueDate,
    );
    if (missingRequired) {
      showError(
        `Certificate Number and Issue Date are required for ${missingRequired.certification}.`,
      );
      return;
    }
    const invalidCertificateNumber = certifications.find(
      (item) => !/^\d+$/.test(item.certificateNumber),
    );
    if (invalidCertificateNumber) {
      showError(
        `Certificate Number must contain numbers only for ${invalidCertificateNumber.certification}.`,
      );
      return;
    }
    const tooLongCertificateNumber = certifications.find(
      (item) => item.certificateNumber.length > 10,
    );
    if (tooLongCertificateNumber) {
      showError(
        `Certificate Number must be at most 10 digits for ${tooLongCertificateNumber.certification}.`,
      );
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    try {
      await Promise.all(
        certifications.map(async (item) => {
          const formData = new FormData();
          formData.append("certificationId", String(item.certificationId));
          formData.append("certificateNumber", item.certificateNumber);
          formData.append("issueDate", item.issueDate);
          if (item.expiryDate) formData.append("expiryDate", item.expiryDate);
          const document = certificateFiles[item.certificationId];
          if (document) formData.append("document", document);

          const response = await fetch(
            `${API_EMPLOYEE_CERTIFICATION}/${encodeURIComponent(
              String(employee.employeeId),
            )}`,
            {
              method: "PUT",
              headers: {
                ...(accessToken()
                  ? { Authorization: `Bearer ${accessToken()}` }
                  : {}),
              },
              body: formData,
            },
          );
          if (!response.ok) throw new Error("Failed to update certification.");
        }),
      );
      showSuccess("Certifications updated successfully.");
      onSaved();
      onClose();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Failed to update certifications.",
      );
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={employee != null}
      onClose={onClose}
      title="Manage Certifications"
      size="xl"
      closeOnEscape
      className={styles.modal}
      bodyClassName={styles.modalBody}
      footer={
        <div className={styles.footer}>
          <Button variant="light-grey" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      }
    >
      {employee ? (
        <div className={styles.content}>
          <section className={styles.employeeSummary}>
            <Avatar
              name={employee.employeeName}
              src={employeePhotoSource}
              size="md"
            />
            <div>
              <h1 className="heading-lg">{employee.employeeName}</h1>
              <p>
                {employee.employeeCode || "-"} ·{" "}
                {employee.designation || "Designation not available"}
              </p>
            </div>
          </section>

          <div className={styles.addRow}>
            <Select
              id="new-certification-category"
              placeholder="Select Category"
              value={category}
              options={categoryOptions}
              onChange={(value) => {
                setCategory(value);
                setCertificationId("");
              }}
              searchable
              isClearable
            />
            <Select
              id="new-certification"
              placeholder={category ? "Select Certification" : "Select Category First"}
              value={certificationId}
              options={certificationOptions}
              onChange={setCertificationId}
              disabled={!category}
              searchable
              isClearable
            />
            <Button
              startIcon={<Plus size={17} aria-hidden />}
              onClick={addCertification}
              disabled={!certificationId}
            >
              Add Certification
            </Button>
          </div>

          {loading ? (
            <div className={styles.loading}>
              <Spinner size="lg" label="Loading certifications" />
            </div>
          ) : certifications.length === 0 ? (
            <div className={styles.empty}>
              No certifications assigned. Use the fields above to add one.
            </div>
          ) : (
            <div className={styles.list}>
              {certifications.map((item, index) => (
                <section
                  className={styles.certification}
                  key={item.employeeCertificationId ?? item.certificationId}
                >
                  <div className={styles.certificationHeader}>
                    <span>
                      <strong>{item.certification}</strong>
                      <small>{formatLabel(item.category)}</small>
                    </span>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => removeCertification(item, index)}
                      disabled={
                        item.employeeCertificationId != null &&
                        deletingIds.has(item.employeeCertificationId)
                      }
                      aria-label={`Delete ${item.certification}`}
                    >
                      <Trash2 size={18} aria-hidden />
                    </button>
                  </div>
                  <div className={styles.fields}>
                    <div className={styles.certificateNumber}>
                      <TextField
                        id={`certification-number-${index}`}
                        label="Certificate Number"
                        required
                        inputMode="numeric"
                        pattern="[0-9]{1,10}"
                        maxLength={10}
                        value={item.certificateNumber}
                        error={
                          item.certificateNumber.length > 10
                            ? "Certificate Number must be at most 10 digits."
                            : item.certificateNumber &&
                                !/^\d+$/.test(item.certificateNumber)
                              ? "Certificate Number must contain numbers only."
                              : undefined
                        }
                        onChange={(event) =>
                          updateField(
                            index,
                            "certificateNumber",
                            certificateNumberDigits(event.target.value),
                          )
                        }
                      />
                    </div>
                    <DatePicker
                      id={`certification-issue-date-${index}`}
                      label="Issue Date"
                      required
                      selected={parseDateOnly(item.issueDate)}
                      onChange={(date) =>
                        updateField(index, "issueDate", formatDateOnly(date))
                      }
                      dateFormat="dd-MMM-yyyy"
                      customInput={<DatePickerFieldTrigger />}
                    />
                    <DatePicker
                      id={`certification-expiry-date-${index}`}
                      label="Expiry Date"
                      selected={parseDateOnly(item.expiryDate)}
                      minDate={parseDateOnly(item.issueDate) ?? undefined}
                      onChange={(date) =>
                        updateField(index, "expiryDate", formatDateOnly(date))
                      }
                      dateFormat="dd-MMM-yyyy"
                      isClearable
                      customInput={<DatePickerFieldTrigger />}
                    />
                    <FileField
                      id={`certification-file-${index}`}
                      label="Upload Certificate"
                      accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                      hint="PDF, PNG, JPG or JPEG"
                      stagedFile={certificateFiles[item.certificationId] ?? null}
                      existingFileName={
                        item.documentPath?.split(/[\\/]/).filter(Boolean).pop() ||
                        (item.certificateUploaded ? "Uploaded certificate" : undefined)
                      }
                      onChange={(file) =>
                        setCertificateFiles((current) => ({
                          ...current,
                          [item.certificationId]: file,
                        }))
                      }
                    />
                    {item.documentPath ? (
                      <Button
                        className={styles.viewButton}
                        variant="muted-outline"
                        startIcon={<Eye size={16} aria-hidden />}
                        onClick={() => void certificateDocument.view(item.documentPath!)}
                        disabled={certificateDocument.loadingPath === item.documentPath}
                      >
                        {certificateDocument.loadingPath === item.documentPath
                          ? "Opening..."
                          : "View Uploaded Certificate"}
                      </Button>
                    ) : null}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      ) : null}
      <CertificationDocumentViewer
        document={certificateDocument.document}
        onClose={certificateDocument.close}
      />
    </Modal>
  );
}
